import { useCallback, useMemo } from 'react'
import { FiatCountryDisabledReason } from '@web/walter-lib/api'
import { ScreenSources } from '@walter/shared/constants/posthog'
import { SWRResponse } from 'swr'
import { useAssetLimits } from '~api'
import { useModal } from '~contexts/ModalsContext'
import { ModalTypes } from '~contexts/ModalsContext/types'
import { SettingsTabs } from '~modals/SettingsModal/Menu/const'
import { ActionDisabledReason } from '~types'
import { useSettingsModal } from '~modals/SettingsModal/useSettingsModal'
import { useTraceContext } from '~contexts/TraceContext'

export interface AssetLimits {
    showModal: (screenSource?: ScreenSources) => void
    goToVerify: (state?: { from: ScreenSources }) => void
    disabled: boolean
    disabledReason: ActionDisabledReason | undefined
    isLoading: boolean
    error: SWRResponse['error']
}

export const useAssetLimitsLogic = (): AssetLimits => {
    const { operationId } = useTraceContext()
    const { data, isValidating: isLoading, error } = useAssetLimits({ operationId })
    const { setTab: setSettingsTab } = useSettingsModal()

    const value = useMemo(() => {
        let reason: ActionDisabledReason | undefined

        if (data?.fiat_country?.disabled) {
            if (data?.fiat_country?.reason === FiatCountryDisabledReason.VERIFICATION_REQUIRED) {
                reason = 'verify'
            } else {
                reason = 'unavailable'
            }
        }

        return {
            disabled: data?.fiat_country?.disabled ?? true,
            disabledReason: reason,
        }
    }, [data?.fiat_country?.disabled, data?.fiat_country?.reason])

    const { show: showAssetLimitsModal } = useModal(ModalTypes.assetLimits)

    const goToVerify = (state?: { from: ScreenSources }) => setSettingsTab(SettingsTabs.identity, state)

    const showModal = useCallback(
        (screenSource?: ScreenSources) => {
            showAssetLimitsModal({ disabledReason: value.disabledReason, screenSource })
        },
        [showAssetLimitsModal, value.disabledReason]
    )

    return { ...value, showModal, isLoading, goToVerify, error }
}
